import React from 'react';
import { Package, Filter, ShieldCheck, Tag, ExternalLink } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели с рекомендациями по оборудованию
 */
const RecommendationsSidebar = ({ 
  formData, 
  results
}) => {
  if (!results) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Package className="w-5 h-5 mr-2 text-indigo-500" />
          Рекомендуемое оборудование
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь появятся рекомендации по оборудованию после выполнения расчета.
        </p>
      </div>
    );
  }

  // Определение рекомендаций по оборудованию на основе объема воды и типа бассейна
  const getEquipmentRecommendations = () => {
    const recommendations = {
      filters: [],
      pumps: [],
      accessories: []
    };
    
    const volume = results.netVolume;
    const isOutdoor = formData.poolLocation === 'outdoor';
    const needsHeating = formData.heatingSystem !== 'none';
    
    // Подбор фильтров по объему бассейна
    if (volume <= 15) {
      if (formData.filterSystem === 'sand') {
        recommendations.filters.push({
          name: 'Песочный фильтр 400-500 мм',
          specs: 'Производительность 6-8 м³/ч',
          price: '12 000 - 20 000 ₽',
          common: true
        });
      } else if (formData.filterSystem === 'cartridge') {
        recommendations.filters.push({
          name: 'Картриджный фильтр 50-70 м²',
          specs: 'Для бассейнов до 15 м³',
          price: '8 000 - 15 000 ₽',
          common: true
        });
      } else {
        recommendations.filters.push({
          name: 'Диатомовый фильтр малой производительности',
          specs: 'Площадь фильтрации 1-2 м²',
          price: '35 000 - 60 000 ₽',
          common: false
        });
      }
    } else if (volume <= 40) {
      if (formData.filterSystem === 'sand') {
        recommendations.filters.push({
          name: 'Песочный фильтр 600-700 мм',
          specs: 'Производительность 10-15 м³/ч',
          price: '22 000 - 35 000 ₽',
          common: true
        });
      } else if (formData.filterSystem === 'cartridge') {
        recommendations.filters.push({
          name: 'Картриджный фильтр 80-100 м²',
          specs: 'Для бассейнов до 40 м³',
          price: '18 000 - 30 000 ₽',
          common: true
        });
      } else {
        recommendations.filters.push({
          name: 'Диатомовый фильтр средней производительности',
          specs: 'Площадь фильтрации 3-4 м²',
          price: '70 000 - 120 000 ₽',
          common: false
        });
      }
    } else {
      if (formData.filterSystem === 'sand') {
        recommendations.filters.push({
          name: 'Песочный фильтр 800-900 мм',
          specs: 'Производительность 15-22 м³/ч',
          price: '40 000 - 65 000 ₽',
          common: true
        });
      } else if (formData.filterSystem === 'cartridge') {
        recommendations.filters.push({
          name: 'Несколько картриджных фильтров или промышленная модель',
          specs: 'Для бассейнов более 40 м³',
          price: '40 000 - 80 000 ₽',
          common: false
        });
      } else {
        recommendations.filters.push({
          name: 'Диатомовый фильтр высокой производительности',
          specs: 'Площадь фильтрации 5+ м²',
          price: '150 000 - 250 000 ₽',
          common: false
        });
      }
    }
    
    // Подбор насосов
    const requiredPumpPower = results.filterRecommendation.pumpPower;
    if (requiredPumpPower < 0.5) {
      recommendations.pumps.push({
        name: 'Насос малой мощности',
        specs: '0.25-0.5 кВт, 4-8 м³/ч',
        price: '7 000 - 15 000 ₽',
        common: true
      });
    } else if (requiredPumpPower < 1.0) {
      recommendations.pumps.push({
        name: 'Насос средней мощности',
        specs: '0.75-1.0 кВт, 8-16 м³/ч',
        price: '15 000 - 25 000 ₽',
        common: true
      });
    } else {
      recommendations.pumps.push({
        name: 'Насос высокой мощности',
        specs: '1.5+ кВт, 16+ м³/ч',
        price: '25 000 - 50 000 ₽',
        common: true
      });
    }
    
    // Для частных бассейнов рекомендуем насос с префильтром
    if (volume < 100) {
      recommendations.pumps.push({
        name: 'Насос с префильтром',
        specs: 'Защита от крупного мусора',
        price: '+3 000 - 5 000 ₽ к стоимости',
        common: true
      });
    }
    
    // Дополнительные аксессуары
    recommendations.accessories.push({
      name: 'Скиммер для очистки поверхности',
      specs: 'Навесной или встраиваемый',
      price: '5 000 - 15 000 ₽',
      common: true
    });
    
    if (isOutdoor) {
      recommendations.accessories.push({
        name: 'Солнечное покрытие',
        specs: 'Снижает испарение на 70%',
        price: `${formatNumber(results.surfaceArea * 2500, 0)} - ${formatNumber(results.surfaceArea * 4000, 0)} ₽`,
        common: true
      });
    }
    
    if (needsHeating) {
      if (formData.heatingSystem === 'electric') {
        recommendations.accessories.push({
          name: 'Электронагреватель',
          specs: `${formatNumber(volume * 1.5, 1)} кВт`,
          price: `${formatNumber(volume * 3000, 0)} - ${formatNumber(volume * 4500, 0)} ₽`,
          common: true
        });
      } else if (formData.heatingSystem === 'heat_pump') {
        recommendations.accessories.push({
          name: 'Тепловой насос для бассейна',
          specs: `${formatNumber(volume * 1.2, 1)} кВт`,
          price: `${formatNumber(volume * 8000, 0)} - ${formatNumber(volume * 12000, 0)} ₽`,
          common: true
        });
      }
    }
    
    // Дозатор хлора для автоматизации
    if (formData.waterType === 'chlorine' && volume > 20) {
      recommendations.accessories.push({
        name: 'Автоматический дозатор хлора',
        specs: 'С контроллером pH и Cl',
        price: '25 000 - 45 000 ₽',
        common: true
      });
    }
    
    return recommendations;
  };
  
  // Получаем рекомендации
  const equipmentRecommendations = getEquipmentRecommendations();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Package className="w-5 h-5 mr-2 text-indigo-500" />
        Рекомендуемое оборудование
      </h3>
      
      {/* Фильтры */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <Filter className="w-4 h-4 mr-1.5 text-indigo-500" />
          Системы фильтрации:
        </h4>
        
        <div className="space-y-3">
          {equipmentRecommendations.filters.map((filter, index) => (
            <div key={index} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 flex justify-between items-center">
                <span className="text-sm font-medium text-indigo-800 dark:text-indigo-300 truncate pr-2">
                  {filter.name}
                </span>
                {filter.common && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    Популярно в РФ
                  </span>
                )}
              </div>
              <div className="p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{filter.specs}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{filter.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Насосы */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Циркуляционные насосы:
        </h4>
        
        <div className="space-y-3">
          {equipmentRecommendations.pumps.map((pump, index) => (
            <div key={index} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-purple-50 dark:bg-purple-900/20 px-3 py-2 flex justify-between items-center">
                <span className="text-sm font-medium text-purple-800 dark:text-purple-300 truncate pr-2">
                  {pump.name}
                </span>
                {pump.common && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    Доступно в РФ
                  </span>
                )}
              </div>
              <div className="p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{pump.specs}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{pump.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Аксессуары */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <Tag className="w-4 h-4 mr-1.5 text-cyan-500" />
          Дополнительное оборудование:
        </h4>
        
        <div className="space-y-3">
          {equipmentRecommendations.accessories.map((accessory, index) => (
            <div key={index} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-cyan-50 dark:bg-cyan-900/20 px-3 py-2">
                <span className="text-sm font-medium text-cyan-800 dark:text-cyan-300">
                  {accessory.name}
                </span>
              </div>
              <div className="p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{accessory.specs}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{accessory.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Ссылка на дополнительную информацию */}
      <div className="mt-5 pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
          <ExternalLink className="w-3 h-3 mr-1.5 flex-shrink-0" />
          Рекомендации основаны на средних ценах и доступности оборудования на российском рынке. 
          Актуальные цены и наличие могут отличаться в зависимости от региона.
        </p>
      </div>
    </div>
  );
};

export default RecommendationsSidebar;